import { environment } from "../../../environments/environment";
import { generateChallenge, generateVerifier } from "./pkce";

const PKCE_VERIFIER_KEY = "pkce_verifier";
const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";

const REDIRECT_URI = `${environment.FRONT_END}/auth-callback`;
const TOKEN_ENDPOINT = `${environment.TGC_USER}/oauth/token`;

// ---------------------------------------------------------------------------
// Token storage
// ---------------------------------------------------------------------------

export function getToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

function storeTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

// ---------------------------------------------------------------------------
// Login — generate PKCE and redirect to authorization server
// ---------------------------------------------------------------------------

export async function login(): Promise<void> {
  const verifier = generateVerifier();
  const challenge = await generateChallenge(verifier);

  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: environment.OAUTH_CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    scope: "openid profile",
    code_challenge: challenge,
    code_challenge_method: "S256",
  });

  window.location.href = `${environment.TGC_USER}/oauth/authorize?${params}`;
}

// ---------------------------------------------------------------------------
// Callback — exchange authorization code for tokens
// ---------------------------------------------------------------------------

export async function handleCallback(code: string): Promise<void> {
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);

  if (!verifier) {
    throw new Error("PKCE verifier not found in session.");
  }

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: environment.OAUTH_CLIENT_ID,
    code,
    code_verifier: verifier,
    redirect_uri: REDIRECT_URI,
  });

  const response = await fetch(TOKEN_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!response.ok) {
    throw new Error("Token exchange failed.");
  }

  const data = await response.json();
  storeTokens(data.access_token, data.refresh_token);
}

// ---------------------------------------------------------------------------
// Refresh — obtain new access token using refresh token
// ---------------------------------------------------------------------------

export async function refresh(): Promise<string> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    throw new Error("No refresh token available.");
  }

  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: environment.OAUTH_CLIENT_ID,
    refresh_token: refreshToken,
  });

  const response = await fetch(TOKEN_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!response.ok) {
    throw new Error("Token refresh failed.");
  }

  const data = await response.json();
  storeTokens(data.access_token, data.refresh_token);
  return data.access_token;
}

// ---------------------------------------------------------------------------
// Token claims — decode JWT payload without verification (display only)
// ---------------------------------------------------------------------------

export interface TokenClaims {
  sub: string;
  email: string;
  name: string;
  roles: string[];
}

export function getTokenClaims(): TokenClaims | null {
  const token = getToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return {
      sub: payload.sub ?? "",
      email: payload.email ?? "",
      name: payload.name ?? payload.email ?? "",
      roles: payload.roles ?? [],
    };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Logout — clear tokens and redirect to backend logout (clears server session)
// ---------------------------------------------------------------------------

export async function logout(): Promise<void> {
  clearTokens();

  const verifier = generateVerifier();
  const challenge = await generateChallenge(verifier);
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);

  const authorizeParams = new URLSearchParams({
    response_type: "code",
    client_id: environment.OAUTH_CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    scope: "openid profile",
    code_challenge: challenge,
    code_challenge_method: "S256",
  });

  const authorizeUrl = `${environment.TGC_USER}/oauth/authorize?${authorizeParams}`;
  window.location.href = `${environment.TGC_USER}/logout?next=${encodeURIComponent(authorizeUrl)}`;
}
